import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import PageManagerTreeViewItem from './tree-view-item.js';
import useDragAutoScroll from '../../hooks/use-drag-auto-scroll.js';

const LEFT_OFFSET = 34;
const TOP_OFFSET = 20;
const SCROLL_CONTAINER_SELECTOR = '.ws-page-manager__content';
// Number of "stacked card" layers shown behind the dragged row to signal that
// several items are being moved at once, and how far each is offset.
const MAX_STACK_LAYERS = 2;
const STACK_LAYER_OFFSET = 4;

/*
  TreeViewItemDragPreview renders the drag preview using createPortal.
  createPortal is necessary to escape any transform: translate3d() on parent elements,
  which would otherwise affect position: fixed positioning and cause the preview
  to appear misaligned from the cursor. By rendering at document.body level,
  the preview positions relative to the viewport (not transformed ancestors),
  ensuring accurate cursor-following behavior.
*/
const TreeViewItemDragPreview = ({
  clientOffset = null,
  item = null,
  isDragging = false,
  count = 1,
}) => {
  useDragAutoScroll(SCROLL_CONTAINER_SELECTOR, isDragging, clientOffset);

  if (!isDragging || !item?.data || !clientOffset) {
    return null;
  }

  const { name, title, password, iconGlyph, isActive } = item.data;
  const stackLayers = Math.min(count - 1, MAX_STACK_LAYERS);

  return createPortal(
    <div
      className="ws-page-manager-tree-view-item-drag-preview"
      style={{
        left: `${clientOffset.x - LEFT_OFFSET}px`,
        top: `${clientOffset.y - TOP_OFFSET}px`,
      }}
    >
      {Array.from({ length: stackLayers }).map((_, index) => {
        // Deeper layers (rendered first) sit further down-right behind the row.
        const depth = stackLayers - index;

        return (
          <div
            // eslint-disable-next-line yola/react/no-array-index-key
            key={index}
            className="ws-page-manager-tree-view-item-drag-preview__stack-layer"
            style={{
              transform: `translate(${depth * STACK_LAYER_OFFSET}px, ${
                depth * STACK_LAYER_OFFSET
              }px)`,
            }}
          />
        );
      })}
      <div className="ws-page-manager-tree-view-item-drag-preview__main">
        <PageManagerTreeViewItem
          isDragging
          name={name}
          title={title}
          password={password}
          iconGlyph={iconGlyph}
          isActive={isActive}
        />
      </div>
    </div>,
    document.body
  );
};

TreeViewItemDragPreview.propTypes = {
  clientOffset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  item: PropTypes.shape({
    data: PropTypes.object,
  }),
  isDragging: PropTypes.bool,
  count: PropTypes.number,
};

export default TreeViewItemDragPreview;
